import ko from "knockout";
import $ from "jquery";
import metaCache from "flow/utils/metaCache";
import "3pp/jquery-clockpicker";
import viewHtml from "./timePicker.html";
import createEnabler from "flow/koHelpers/createEnablerDisabler";
import { Dictionary } from "flow/api/types";
import { MaybeObservable, MaybeObservableAttributes } from "flow/interfaces/MaybeObservable";
import createRequiredObservable from "flow/koHelpers/createRequiredObservable";
import { selectIdStatic } from "flow/koHelpers/selectId";
import { selectLabel, selectPlaceholder } from "flow/koHelpers/selectParam";
import { interceptAttributesWithDefaultsIgnoreId } from "flow/koHelpers/interceptAttributes";

type ClockPosition = "bottom" | "top" | "left" | "right";

export interface IClockPickerOptions {
    default?: "now" | string;
    placement?: ClockPosition;
    align?: ClockPosition;
    donetext?: string;
    autoclose?: boolean;
    twelvehour?: boolean;
    vibrate?: boolean;
    fromnow?: number;

    // Quick typing without the callbacks since they will probably cause problems, jquery ui + knockout is troublesome enough
    // https://github.com/weareoutman/clockpicker
}

interface IClockPickerControl {
    clockpicker(options: IClockPickerOptions): ClockPickableJQueryElement;
    clockpicker(operation: "show" | "hide" | "remove"): ClockPickableJQueryElement;
    clockpicker(operation: "toggleView", view: "hours" | "minutes"): ClockPickableJQueryElement;
}

type ClockPickableJQueryElement = JQuery<Element> & IClockPickerControl;

export interface ITimePickerParams {
    time: KnockoutObservable<string>;
    validationElement?: KnockoutObservable<any>; // The observable to add the validation error on
    clockpickerOptions?: IClockPickerOptions;
    disable?: MaybeObservable<boolean>;
    enable?: MaybeObservable<boolean>;
    metaPlaceholder?: MaybeObservable<string>;
    placeholder?: MaybeObservable<string>;
    buttonFocusable?: boolean;
    attr?: MaybeObservableAttributes;
    meta?: MaybeObservable<string>;
    text?: MaybeObservable<string>;
}

class TimePickerViewModel {
    time: KnockoutObservable<string>;
    enable: MaybeObservable<boolean>;
    attributes: Dictionary;
    buttonAttributes: Dictionary;
    validationElement: KnockoutObservable<any>;
    $clockPicker: ClockPickableJQueryElement;
    hour: string;
    previousMinutes: string;
    completeTimeSet: boolean;
    label: MaybeObservable<string>;
    id: MaybeObservable<string>;
    required: MaybeObservable<boolean>;

    constructor(params: ITimePickerParams, $clockPicker: ClockPickableJQueryElement) {
        this.time = params.time.extend({ cultureTime: true });
        this.$clockPicker = $clockPicker;
        this.required = createRequiredObservable(params.time);
        this.id = selectIdStatic("ko_timepicker", params.attr);
        this.label = selectLabel(params.meta, params.text);
        this.enable = createEnabler(params.enable, params.disable);
        this.attributes = interceptAttributesWithDefaultsIgnoreId(
            {
                autocomplete: "off",
                placeholder: selectPlaceholder(params.metaPlaceholder, params.placeholder)
            },
            params.attr
        );
        this.$clockPicker.attr("id", this.id);

        this.completeTimeSet = false;

        this.validationElement = params.validationElement || params.time;

        const paramOptions: IClockPickerOptions = ko.unwrap(params.clockpickerOptions || {});

        this.buttonAttributes = {
            tabindex: params.buttonFocusable ? null : "-1"
        };

        const options: IClockPickerOptions = Object.assign(
            {
                donetext: metaCache.lookup(paramOptions.donetext, "OK"),
                showOnFocus: false,
                afterHourSelect: this.afterHourSelect,
                beforeDone: this.afterMinuteSelect,
                afterHide: this.afterHide,
                autoclose: true
            },
            paramOptions
        );

        this.$clockPicker.on("change", this.syncFromElementToObservable);
        this.$clockPicker.clockpicker(options);
    }

    syncFromElementToObservable = () => {
        this.time(this.$clockPicker.val() as string);
    };

    onTimeChanged = () => {
        this.$clockPicker.clockpicker("hide");
    };

    afterHourSelect = () => {
        this.hour = $(".clockpicker-popover:visible .clockpicker-span-hours").html();
        this.previousMinutes = $(".clockpicker-popover:visible .clockpicker-span-minutes").html();
    };

    afterMinuteSelect = () => {
        this.completeTimeSet = true;
    };

    afterHide = () => {
        if (!this.completeTimeSet) {
            if (this.hour) {
                this.time(`${this.hour}:${this.previousMinutes}`);
            }
        }
        this.completeTimeSet = false;
        this.hour = "";
    };

    onClockButtonClicked = () => {
        this.$clockPicker.clockpicker("show");
    };

    dispose = () => {
        this.$clockPicker.clockpicker("remove");
    };
}

const component = {
    viewModel: {
        createViewModel(params: ITimePickerParams, componentInfo: KnockoutComponentTypes.ComponentInfo) {
            const input = $(componentInfo.element as Element)
                .find("input")
                .first() as any as ClockPickableJQueryElement;
            return new TimePickerViewModel(params, input);
        }
    },
    template: viewHtml,
    synchronous: true
};

ko.components.register("ko-timepicker", component);
ko.components.register("ko-time-picker", component);
